import type { Classroom, SeatingChart, Student } from "@classroom/domain";
import { useTranslation } from "./i18n";
import { sortClasses } from "./lib/classOrder";
import { rosterClassId, setupProgress } from "./lib/classSharing";

export default function ClassSetupFollowUp({
  classroom,
  classes,
  students,
  charts,
  onNext,
  onStart,
}: {
  classroom: Classroom;
  classes: Classroom[];
  students: Student[];
  charts: SeatingChart[];
  onNext: (classroom: Classroom) => void;
  onStart: () => void;
}) {
  const { t } = useTranslation("classSetupStatus");
  const current = setupProgress(classroom, classes, students, charts);
  // Shared role entries require only one roster and seating setup.
  const seen = new Set([rosterClassId(classroom, classes)]);
  const pending = sortClasses(classes).filter((item) => {
    if (!item.active || setupProgress(item, classes, students, charts).hasSeating) return false;
    const sourceId = rosterClassId(item, classes);
    if (seen.has(sourceId)) return false;
    seen.add(sourceId);
    return true;
  });
  const missingRoster = pending.filter((item) => !setupProgress(item, classes, students, charts).hasRoster).length;
  const missingSeating = pending.length - missingRoster;

  return (
    <section className="paper-card class-setup-follow-up" aria-label={t("followUpRegion")}>
      <div className="class-setup-follow-up-copy">
        <h2>{t(current.hasSeating ? "classComplete" : "tutorialComplete", { name: classroom.name })}</h2>
        {!current.hasSeating && <p>{t("continueCurrentSeating")}</p>}
        {pending.length > 0 ? (
          <p>
            {t("otherClasses")}
            {[
              missingRoster > 0 ? t("missingRosterCount", { count: missingRoster }) : "",
              missingSeating > 0 ? t("missingSeatingCount", { count: missingSeating }) : "",
            ]
              .filter(Boolean)
              .join("、")}
            {t("canSetUpLater")}
          </p>
        ) : (
          <p>{t("noOtherClasses")}</p>
        )}
      </div>
      <div className="class-setup-follow-up-actions">
        {pending[0] && (
          <button type="button" className="primary-action" onClick={() => onNext(pending[0]!)}>
            {t("nextClass")}
          </button>
        )}
        <button type="button" onClick={onStart}>
          {t("startUsing")}
        </button>
      </div>
    </section>
  );
}
